import { EntityKindTypeahead } from '@internal/backstage-plugin-accentuate';
import React, { useState, useMemo } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';

import { useSubmitRevisions } from './hooks/useProposedOwnershipRepos';
import { useReviseOwnershipModalStyles } from './styles/ReviseOwnershipModal.styles';
import { ProposedOwnershipRepo, RevisionChange } from './types';
import { pluralizePrefix } from './utils';

interface ReviseOwnershipModalProps {
  open: boolean;
  onClose: () => void;
  repositories: ProposedOwnershipRepo[];
  onSuccess: (revisedCount: number) => void;
}

export const ReviseOwnershipModal = ({
  open,
  onClose,
  repositories,
  onSuccess,
}: ReviseOwnershipModalProps) => {
  const classes = useReviseOwnershipModalStyles();
  const { submit } = useSubmitRevisions();

  // Track changes for each repository by ID
  const [changes, setChanges] = useState<Record<string, RevisionChange>>({});
  // Track removed repository IDs
  const [removedRepoIds, setRemovedRepoIds] = useState<Set<string>>(new Set());
  // Track loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTeamChange = (repoId: string, teamRef: string | undefined) => {
    setChanges(prev => ({
      ...prev,
      [repoId]: {
        ...prev[repoId],
        teamEntityRef: teamRef,
        markAsUnknown: false, // Uncheck unknown if team is selected
      },
    }));
  };

  const handleMarkAsUnknownChange = (repoId: string, checked: boolean) => {
    setChanges(prev => ({
      ...prev,
      [repoId]: {
        ...prev[repoId],
        markAsUnknown: checked,
        teamEntityRef: checked ? undefined : prev[repoId]?.teamEntityRef,
      },
    }));
  };

  const handleRemoveRepository = (repoId: string) => {
    setRemovedRepoIds(prev => new Set([...prev, repoId]));
    // Also remove any changes for this repository
    setChanges(prev => {
      const updated = { ...prev };
      delete updated[repoId];
      return updated;
    });
  };

  const handleClose = () => {
    setChanges({});
    setRemovedRepoIds(new Set());
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Filter out removed repositories
      const activeRepositories = repositories.filter(
        repo => !removedRepoIds.has(repo.id),
      );

      await submit(activeRepositories, changes);

      // Success: close modal and refresh list
      const revisedCount = activeRepositories.length;
      handleClose();
      onSuccess(revisedCount);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to submit revisions',
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter repositories to show (excluding removed ones)
  const activeRepositories = useMemo(() => {
    return repositories.filter(repo => !removedRepoIds.has(repo.id));
  }, [repositories, removedRepoIds]);

  // Check if all active repositories have necessary information provided
  const isFormValid = useMemo(() => {
    // If no active repositories, form is invalid
    if (activeRepositories.length === 0) {
      return false;
    }

    // Every active repository must have either a new team or be marked as unknown
    return activeRepositories.every(repo => {
      const change = changes[repo.id];
      return change && (change.teamEntityRef || change.markAsUnknown);
    });
  }, [activeRepositories, changes]);

  // Auto-close if all repositories are removed
  React.useEffect(() => {
    if (open && repositories.length > 0 && activeRepositories.length === 0) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, repositories.length, activeRepositories.length]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          Revise Ownership Recommendations
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {error && (
          <Box className={classes.errorBox}>
            <Typography className={classes.errorText}>
              Error: {error}
            </Typography>
          </Box>
        )}

        <Box className={classes.optionsInfo}>
          <Box className={classes.optionItem}>
            <Typography className={classes.optionLabel}>
              <strong>Select a new team:</strong>
            </Typography>
            <Typography className={classes.optionDescription}>
              The selected team will be prompted to confirm ownership.
            </Typography>
          </Box>
          <Box className={classes.optionItem}>
            <Typography className={classes.optionLabel}>
              <strong>Mark as unknown:</strong>
            </Typography>
            <Typography className={classes.optionDescription}>
              The parent team of the current proposed team will be prompted to
              confirm.
            </Typography>
          </Box>
        </Box>

        <Box className={classes.tableContainer}>
          <table className={classes.table}>
            <thead className={classes.tableHeader}>
              <tr>
                <th className={classes.tableHeaderCell}>Entity</th>
                <th className={classes.tableHeaderCell}>
                  Current Proposed Team
                </th>
                <th className={classes.tableHeaderCell}>New Proposed Team</th>
                <th className={classes.tableHeaderCell} style={{ width: 50 }} />
              </tr>
            </thead>
            <tbody>
              {activeRepositories.map(repo => {
                const change = changes[repo.id] || {
                  markAsUnknown: false,
                  teamEntityRef: undefined,
                };

                return (
                  <tr key={repo.id} className={classes.tableRow}>
                    <td className={classes.tableCell}>
                      <Box className={classes.repositoryInfo}>
                        <Link
                          href={`https://github.com/tomtom-internal/${repo.repositoryName}`}
                          className={classes.repositoryName}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {repo.entity}
                        </Link>
                      </Box>
                    </td>
                    <td className={classes.tableCell}>
                      <Box className={classes.currentTeam}>
                        {repo.teamRecommendation}
                      </Box>
                    </td>
                    <td className={classes.tableCell}>
                      <Box className={classes.controlsCell}>
                        <Box className={classes.controlsRow}>
                          <Box className={classes.teamSelectContainer}>
                            <EntityKindTypeahead
                              id={`team-${repo.id}`}
                              name={`team-${repo.id}`}
                              schema={{}}
                              value={change.teamEntityRef}
                              onChange={value =>
                                handleTeamChange(repo.id, value)
                              }
                              onBlur={() => {}}
                              onFocus={() => {}}
                              registry={{} as any}
                              label="Select New Team"
                              options={{
                                allowedKinds: ['Group'],
                                filterBySpecType: 'supervisory-org',
                              }}
                              disabled={change.markAsUnknown}
                            />
                          </Box>
                          <Box className={classes.checkboxContainer}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={change.markAsUnknown}
                                  onChange={e =>
                                    handleMarkAsUnknownChange(
                                      repo.id,
                                      e.target.checked,
                                    )
                                  }
                                  color="primary"
                                  size="small"
                                />
                              }
                              label={
                                <Typography className={classes.checkboxLabel}>
                                  Mark as Unknown
                                </Typography>
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    </td>
                    <td className={classes.tableCell}>
                      <Tooltip title="Remove from list">
                        <IconButton
                          className={classes.removeButton}
                          onClick={() => handleRemoveRepository(repo.id)}
                          size="small"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Box className={classes.actionsLeft}>
          <Typography className={classes.selectionInfo}>
            {pluralizePrefix('repositor', activeRepositories.length)}{' '}
            {activeRepositories.length !== repositories.length &&
              `(${repositories.length - activeRepositories.length} removed)`}
          </Typography>
        </Box>
        <Box className={classes.actionsRight}>
          <Button onClick={handleClose} className={classes.cancelButton}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className={classes.submitButton}
            variant="contained"
            color="primary"
            disabled={!isFormValid || loading}
            startIcon={<EditIcon />}
          >
            {loading ? 'Submitting...' : 'Submit Revisions'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
